import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  useWindowDimensions,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  StyleSheet,
} from 'react-native';
import Popover from 'react-native-popover-view/dist/Popover';
import { useIsFocused } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import moment from 'moment';

import { config } from '../../shared/environment';
import CommonHeader from '../../shared/commonComponents/commonHeader';
import { appColors, appFonts, fontSize } from '../../themes/appTheme';
import ZenText from 'zen-core-elements/src/elements/zenText/zenText';
import { deleteConfirmationProps } from '../role/roleModule';
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox';
import {
  ZenBadge,
  ZenEmptyComponent,
  ZenFilterModal,
  ZenIcon,
  ZenLoader,
  ZenToast,
} from 'zen-core-elements';
import {
  FilterPayloadProps,
  PropertyValue,
  Role,
  setInivitationProps,
} from './invitedStaffModule';
import { filterItemsProps } from 'zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal';
import {
  deletePosInvitation,
  invitationMail,
  invitedRoles,
  posInvitations,
  propertyValuesByCode,
} from '../../redux/slices/invitedStaffSlice';
import { ListToolbar } from '../../shared/commonComponents/listToolbar';

const LIMIT = 10;

const InvitedStaff = () => {
  const dispatch = useAppDispatch();
  const { width } = useWindowDimensions();
  const isFocused = useIsFocused();

  // ── Local UI state ───────────────────────────────────────────────────
  const [search, setSearch] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [footerLoad, setFooterLoad] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [filterPayLoad, setFilterPayLoad] = useState<FilterPayloadProps>(null);
  const [count, setCount] = useState<number>(0);
  const [deletePopover, setDeletePopover] = useState<boolean>(false);
  const [invitationId, setInvitationId] = useState<number>(0);
  const [deleteInvitation, setDeleteInvitation] = useState<deleteConfirmationProps>();

  // ── Redux cached state ───────────────────────────────────────────────
  const cachedRows = useAppSelector(state => state.invitedStaff.posInvitations.rows,);
  const cachedCount = useAppSelector(state => state.invitedStaff.posInvitations.count,);
  const isReached = useAppSelector(state => state.invitedStaff.posInvitations.isReached,);
  const cachedFilter = useAppSelector(state => state.invitedStaff.posInvitations.filter);
  const cachedRoles = useAppSelector(state => state.invitedStaff.invitedRoles);
  const cachedProperty = useAppSelector(state => state.invitedStaff.propertyValuesByCode,);
  const loading = useAppSelector(state => state.invitedStaff.loading);

  // ── Filter state ─────────────────────────────────────────────────────
  const [filterData, setFilterData] = useState<filterItemsProps[]>([
    {
      id: '1',
      filterName: 'Status',
      filterType: 'radio',
      initialSelected: '',
      filterValue: [],
    },
    {
      id: '2',
      filterName: 'Role',
      filterType: 'dropdown',
      initialSelected: '',
      filterValue: [],
    },
    {
      id: '3',
      filterName: 'Invited Date',
      filterType: 'dateRange',
      initialSelected: { startDate: '', endDate: '' },
      filterValue: { minDate: '', maxDate: '' },
    },
  ]);

  // ── Sync filter dropdowns from Redux cache if already populated ──────
  useEffect(() => {
    if (cachedRoles?.roles?.rows && cachedProperty?.propertyValue) {
      const roleFilter: { value: string; label: string }[] = [];
      cachedRoles.roles.rows.forEach((e: Role) => {
        roleFilter.push({ value: e.id.toString(), label: e.name });
      });

      const statusFilter: { value: string; label: string }[] = [];
      cachedProperty.propertyValue.propertyValues.forEach(
        (e: PropertyValue) => {
          statusFilter.push({ value: e?.id.toString(), label: e?.value });
        },
      );

      setFilterData(prev => {
        const val = [...prev];
        val.forEach((v: any) => {
          if (v.filterName === 'Role') v.filterValue = roleFilter;
          if (v.filterName === 'Status') v.filterValue = statusFilter;
        });
        return val;
      });
    }
  }, [cachedRoles, cachedProperty]);

  // ── Initial load on focus ────────────────────────────────────────────
  useEffect(() => {
    if (isFocused && cachedFilter === null) {
      fetchInitialData();
    }
  }, [isFocused]);

  // ── Fetch if filter fingerprint changed (tab switch re-sync) ─────────
  useEffect(() => {
    const currentFingerprint = buildFingerprint(search ?? "", filterPayLoad);
    if (cachedFilter !== currentFingerprint && cachedFilter !== null) {
      setOffset(0);
      getPosInvitationsAPI(0, search ?? "", filterPayLoad);
    }
  }, [filterPayLoad, cachedFilter]);

  // ── Search debounce ──────────────────────────────────────────────────
  useEffect(() => {
    if (search) {
      setOffset(0);
      const debounce = setTimeout(() => {
        getPosInvitationsAPI(0, search, filterPayLoad, true);
      }, 1000);
      return () => clearTimeout(debounce);
    } else if (search === '') {
      getPosInvitationsAPI(0, search, filterPayLoad, true);
    }
  }, [search]);

  // ── Delete confirmation listener ─────────────────────────────────────
  useEffect(() => {
    if (deleteInvitation) {
      if (deleteInvitation?.name === 'Yes') {
        onDelete(invitationId);
        setDeletePopover(false);
      } else {
        setDeletePopover(false);
      }
    }
  }, [deleteInvitation]);

  // ── Helpers ──────────────────────────────────────────────────────────
  const buildFingerprint = (
    searchText: string,
    fp: FilterPayloadProps,
  ) => {
    // Must match the fingerprint stored by the Redux slice:
    // state.posInvitations.filter = JSON.stringify({ searchText, filterData })
    // where filterData is the raw filterDataStr (a JSON string or null), NOT double-serialized.
    const filterDataStr = fp ? buildFilterData(fp) : null;
    return JSON.stringify({
      searchText: searchText ?? '',
      filterData: filterDataStr,
    });
  };

  const buildFilterData = (fp: FilterPayloadProps): string =>
    JSON.stringify({
      createdAt: {
        begin: fp?.dateRange?.startDate
          ? moment(fp.dateRange.startDate).format('YYYY-MM-DD')
          : null,
        end: fp?.dateRange?.endDate
          ? moment(fp.dateRange.endDate).format('YYYY-MM-DD')
          : null,
      },
      statusId: fp?.status ?? null,
      roleId: fp?.role ?? null,
    });

  // ── Bootstrap: fetch roles → status → invitations ───────────────────
  const fetchInitialData = () => {
    if (cachedRoles?.roles?.rows && cachedProperty?.propertyValue) {
      // Already have lookup data; go straight to list
      getPosInvitationsAPI(0, '', null);
      return;
    }
    dispatch(invitedRoles({ storeId: config.storeId }))
      .unwrap()
      .then(res => {
        if (res?.roles?.rows) {
          const roleFilter: { value: string; label: string }[] = [];
          res.roles.rows.forEach((e: Role) => {
            roleFilter.push({ value: e.id.toString(), label: e.name });
          });
          setFilterData(prev => {
            const val = [...prev];
            val.forEach((v: any) => {
              if (v.filterName === 'Role') v.filterValue = roleFilter;
            });
            return val;
          });
          fetchPropertyValues();
        }
      })
      .catch(() => console.log('error in get invited roles'));
  };

  const fetchPropertyValues = () => {
    dispatch(propertyValuesByCode({ code: 'INVITATION_STATUS' }))
      .unwrap()
      .then(val => {
        if (val?.propertyValue) {
          const statusFilter: { value: string; label: string }[] = [];
          val.propertyValue.propertyValues.forEach((e: PropertyValue) => {
            statusFilter.push({ value: e.id.toString(), label: e?.value });
          });
          setFilterData(prev => {
            const updated = [...prev];
            updated.forEach((v: any) => {
              if (v.filterName === 'Status') v.filterValue = statusFilter;
            });
            return updated;
          });
          getPosInvitationsAPI(0, '', null, true);
        }
      })
      .catch(() => console.log('error in get property values'));
  };

  // ── Core API call ────────────────────────────────────────────────────
  const getPosInvitationsAPI = (
    offsetVal: number,
    searchText: string,
    fp: FilterPayloadProps,
    forceRefresh = false,
  ) => {
    const filterDataStr = fp ? buildFilterData(fp) : null;
    const payload = {
      offset: offsetVal,
      limit: LIMIT,
      searchText: searchText ?? '',
      filterData: filterDataStr,
    };
    const currentFingerprint = buildFingerprint(searchText, fp);

    if (!forceRefresh && offsetVal === 0 && cachedFilter === currentFingerprint) {
      return;
    }

    dispatch(posInvitations({ storeId: config.storeId, data: payload }))
      .unwrap()
      .catch(err => console.log('error in get pos invitations', err))
      .finally(() => {
        setFooterLoad(false);
        setRefresh(false);
      });
  };

  // ── Pagination ───────────────────────────────────────────────────────
  const handleEndReached = () => {
    if (!isReached && !footerLoad && !loading && cachedRows.length < cachedCount) {
      const nextOffset = offset + LIMIT;
      setOffset(nextOffset);
      setFooterLoad(true);
      getPosInvitationsAPI(nextOffset, search ?? "", filterPayLoad);
    }
  };

  // ── Pull-to-refresh ──────────────────────────────────────────────────
  const handleRefresh = () => {
    setRefresh(true);
    setOffset(0);
    getPosInvitationsAPI(0, search ?? "", filterPayLoad, true);
  };

  // ── Delete invitation ────────────────────────────────────────────────
  const onDelete = async (id: number) => {
    await dispatch(
      deletePosInvitation({ storeId: config.storeId, invitationId: id }),
    )
      .unwrap()
      .then(res => {
        if (res) {
          getPosInvitationsAPI(0, search ?? "", filterPayLoad, true);
        }
      })
      .catch(err => console.log('error in removing staff', err));
  };

  // ── Resend invitation ────────────────────────────────────────────────
  const onResendInvitation = async (item: setInivitationProps) => {
    const dataValue = {
      emails: [item?.email],
      role:
        item?.staffLoginInvitation && item?.staffLoginInvitation.pos_role
          ? item?.staffLoginInvitation.pos_role
          : null,
      staffLoginInvitationId: item?.staffLoginInvitation?.id,
    };
    await dispatch(
      invitationMail({
        storeId: config.storeId,
        invitationId: item?.staffLoginInvitation?.id,
        data: dataValue,
      }),
    )
      .unwrap()
      .then(response => {
        if (response) {
          ZenToast({ type: 'success', message: 'Invitation sent successfully' });
        }
      })
      .catch(err => console.log('Error in resending invitation', err));
  };

  // ── Badge color helper ───────────────────────────────────────────────
  const getBadgeColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return appColors.secondary;
      case 'Removed Staff':
        return appColors.red;
      case 'Removed Role':
        return appColors.wildBlue;
      case 'Staff Approved':
        return appColors.green;
      default:
        return appColors.badgeActive;
    }
  };

  // ── Render item ──────────────────────────────────────────────────────
  const renderItem = ({ item }: { item: setInivitationProps }) => {
    return (
      <View style={styles.card}>
        {/* Status badge + date row */}
        <View style={styles.topRow}>
          <ZenBadge
            badgeName={item?.status}
            badgeTextColor={appColors?.light}
            badgeColor={getBadgeColor(item?.status)}
            badgeTextSize={'small'}
            badgeTextStyle={{ fontFamily: appFonts.bold }}
          />
          <ZenText
            content={moment(item?.createdAt).format('DD/MM/YYYY')}
            color={appColors.lightDark}
            bold={true}
            style={{ fontSize: fontSize?.smallHeaderFontSize }}
          />
        </View>

        <View style={styles.divider} />

        {/* Details row */}
        <View style={styles.detailsRow}>
          <View style={styles.detailCol}>
            <ZenText
              content="Email"
              color={appColors.labelColor}
              style={{ fontSize: fontSize?.smallerFontSize }}
            />
            <ZenText
              content={item?.email ?? 'N/A'}
              color={appColors.lightDark}
              bold={true}
              style={{ fontSize: fontSize?.smallFontSize, fontFamily: appFonts?.bold }}
              numberOfLines={1}
            />
          </View>

          <View style={styles.detailCol}>
            <ZenText
              content="Invited By"
              color={appColors.labelColor}
              style={{ fontSize: fontSize?.smallerFontSize }}
            />
            <ZenText
              content={item?.calledBy?.name ?? 'N/A'}
              color={appColors.lightDark}
              bold={true}
              style={{ fontSize: fontSize?.smallFontSize, fontFamily: appFonts?.bold }}
            />
          </View>

          {/* Action buttons */}
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <TouchableOpacity
              style={styles.iconBtn}
              onPress={() => onResendInvitation(item)}>
              <ZenIcon name="email-send-outline" type="material-community" size={16} color={appColors.iconColor} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.iconBtn, { marginLeft: 10 }]}
              onPress={() => {
                setDeletePopover(true);
                setInvitationId(item?.id);
              }}>
              <ZenIcon name="delete-outline" type="material-community" size={16} color={appColors.iconColor} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={{ flex: 1, backgroundColor: 'whitesmoke' }}>
      <CommonHeader headerName="Invited Staff" />
      <ListToolbar
        search={{
          value: search ?? "",
          onChange: (val) => setSearch(val as string),
          onCancel: () => setSearch(null),
          placeholder: 'Search by Staff Name',
        }}
        filter={{
          count: count ?? 0,
          onPress: () => setIsVisible(true),
        }}
      />

      {loading && !offset && (!refresh || !cachedRows.length) ? (
        <ZenLoader
          lottieType="Local"
          lottie={require('../../assets/infinityLoader.json')}
        />
      ) : (
        <View style={{ flex: 1 }}>
          <FlatList
            data={cachedRows}
            keyExtractor={item => item.id.toString()}
            renderItem={renderItem}
            contentContainerStyle={styles.listContent}
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.5}
            refreshControl={<RefreshControl refreshing={refresh} onRefresh={handleRefresh} colors={[appColors.primary]} />}
            ListFooterComponent={footerLoad ? (<ActivityIndicator size="small" color={appColors.primary} style={{ paddingVertical: 12 }} />) : null}
            ListEmptyComponent={ZenEmptyComponent}
          />
        </View>
      )}
      <ZenFilterModal
        setVisible={setIsVisible}
        visible={isVisible}
        filterItems={filterData}
        setFilterItems={setFilterData}
        filterCount={count}
        setFilterCount={setCount}
        title="Invited Staff Filter"
        isSingleExpand={false}
        onConfirm={val => {
          let payload: FilterPayloadProps = {};
          if (val?.values?.['Invited Date']) {
            payload.dateRange = val?.values?.['Invited Date'];
          }
          if (val?.values?.Role) {
            payload.status = val?.values?.Role;
          }
          if (val?.values?.Status) {
            payload.role = val?.values?.Status;
          }
          setFilterPayLoad(payload);
          setIsVisible(false);
        }}
      />
      <Popover
        isVisible={deletePopover}
        popoverStyle={{
          width: width > 900 ? 600 : width > 600 ? 500 : 300,
          borderRadius: 10,
        }}>
        <CommonDialogBox
          type="DELETE"
          replaceValue="invitation"
          confirmation={setDeleteInvitation}
        />
      </Popover>
    </View>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  listContent: {
    paddingHorizontal: 10,
    paddingBottom: 20,
    flexGrow: 1,
  },
  card: {
    padding: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.borderColor,
    backgroundColor: appColors.light,
    marginVertical: 5,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  divider: {
    height: 1,
    backgroundColor: appColors.borderColor,
    marginVertical: 10,
  },
  detailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: 5,
  },
  detailCol: {
    rowGap: 5,
  },
  iconBtn: {
    padding: 10,
    backgroundColor: appColors.lightBackgroundColor,
    borderRadius: 10,
    alignSelf: 'flex-end',
  },
});

export default InvitedStaff;
